/**
 * The OpenFeature evaluation context the client UI hands Datadog, built from the auth module.
 *
 * The attribute names are the backend `SessionInfoEvaluationHook`'s, so one targeting rule in
 * Datadog means the same thing whether the browser or a service evaluates the flag. Every
 * attribute is a flat primitive: Datadog targets on top-level attributes only.
 *
 * The context is built from client state the user can edit, so it decides what the UI SHOWS and
 * nothing else. Anything that must hold regardless is gated on the backend with the same key.
 */

// Cognito group -> context attribute. Mirrors `FeatureFlagRoleAttributes` in ringba-x.
export const ROLE_ATTRIBUTES = Object.freeze({
  Admin: 'roleAdmin',
  Buyer: 'roleBuyer',
  Seller: 'roleSeller',
  FinancialManager: 'roleFinancialManager',
  AccountManager: 'roleAccountManager',
  AccountManagerAdmin: 'roleAccountManagerAdmin'
})

/**
 * @param {object} auth the auth module's getters, as plain values
 * @param {boolean} auth.isAuthenticated
 * @param {string} [auth.userId]
 * @param {string} [auth.accountId] the current account; the impersonated one when impersonating
 * @param {string[]} [auth.groups] `userDetails.groups`
 * @param {boolean} [auth.isImpersonating]
 * @returns {import('@openfeature/web-sdk').EvaluationContext | null} `{}` (anonymous) without a
 *   session, and `null` while a session's user details are still loading. The plugin sends neither
 *   to Datadog: no flag targets a signed-out visitor.
 */
export function buildEvaluationContext({ isAuthenticated, userId, accountId, groups, isImpersonating }) {
  // `logout` drops the tokens straight away but leaves `userDetails` until the login page resets
  // the store, so the tokens are what says the session is over.
  if (!isAuthenticated) {
    return {}
  }

  // A token and no user yet: a page load with a stored session, or a login, whose user details are
  // on their way. Not anonymous, and not anyone in particular either.
  if (!userId) {
    return null
  }

  const userGroups = Array.isArray(groups) ? groups : []

  const context = {
    // Keyed by account so a percentage rollout buckets accounts, the same way the backend does.
    // A user with no current account (a super admin on the Impersonate page) stands in for one.
    targetingKey: accountId || userId,
    userId,
    isImpersonating: Boolean(isImpersonating)
  }

  if (accountId) {
    context.accountId = accountId
  }

  for (const [group, attribute] of Object.entries(ROLE_ATTRIBUTES)) {
    context[attribute] = userGroups.includes(group)
  }

  return context
}

/**
 * A stable string for a context, to tell whether it has changed since it was last sent.
 * `buildEvaluationContext` always adds its keys in the same order, so `JSON.stringify` is enough.
 */
export function contextSignature(context) {
  return JSON.stringify(context)
}

export function isAnonymousContext(context) {
  return Object.keys(context).length === 0
}
